import { Edge, Node } from "@xyflow/react";
import Take from "../../../models/Take";
import Take3D from "../../../models/Take3D";
import Interaction, { InteractionType } from "../../../models/interactions/Interaction";
import {
  ISequence,
  SequenceCommentZone,
  SequenceNodeData,
  SequenceTextbox,
} from "../../../models/interactions/SequenceModel";

export type SequenceLocator =
  | { kind: "interaction"; takeId: string; sequenceId: string }
  | { kind: "takeSequence"; takeId: string; sequenceId: string };

export type SequenceSelectionIds = {
  nodeIds: string[];
  commentZoneIds: string[];
  textboxIds: string[];
};

export type MoveSequenceSelectionPayload = {
  source: SequenceLocator;
  destination: SequenceLocator;
  selection: SequenceSelectionIds;
  targetOrigin: { x: number; y: number };
};

export type SequenceMoveFailure =
  | "missingSource"
  | "missingDestination"
  | "missingItems"
  | "emptySelection"
  | "destinationCollision";

export type SequenceMoveResolution =
  | {
      ok: true;
      takes: Take[];
      movedNodes: Node<SequenceNodeData>[];
      movedCommentZones: SequenceCommentZone[];
      movedTextboxes: SequenceTextbox[];
    }
  | { ok: false; reason: SequenceMoveFailure };

type QuizFeedbackInteraction = Interaction & {
  correctFeedbackInteractions?: Interaction[];
  wrongFeedbackInteractions?: Interaction[];
};

function mapInteractionTree(
  interaction: Interaction,
  mapper: (interaction: Interaction) => Interaction,
): Interaction {
  const mapped = mapper(interaction) as QuizFeedbackInteraction;
  const correct = mapped.correctFeedbackInteractions;
  const wrong = mapped.wrongFeedbackInteractions;
  if (!correct && !wrong) return mapped;

  return {
    ...mapped,
    ...(correct && {
      correctFeedbackInteractions: correct.map((child) =>
        mapInteractionTree(child, mapper),
      ),
    }),
    ...(wrong && {
      wrongFeedbackInteractions: wrong.map((child) =>
        mapInteractionTree(child, mapper),
      ),
    }),
  } as Interaction;
}

function findInteractionSequence(
  interactions: Interaction[],
  sequenceId: string,
): ISequence | undefined {
  for (const interaction of interactions) {
    if (
      interaction.id === sequenceId &&
      interaction.type === InteractionType.Sequence
    ) {
      return interaction as ISequence;
    }

    const feedback = interaction as QuizFeedbackInteraction;
    const nested = findInteractionSequence(
      [
        ...(feedback.correctFeedbackInteractions ?? []),
        ...(feedback.wrongFeedbackInteractions ?? []),
      ],
      sequenceId,
    );
    if (nested) return nested;
  }
  return undefined;
}

export function resolveSequenceLocator(
  takes: Take[],
  locator: SequenceLocator,
): ISequence | undefined {
  const take = takes.find((candidate) => candidate.id === locator.takeId);
  if (!take) return undefined;

  if (locator.kind === "takeSequence") {
    const sequence = (take as Take3D).sequence;
    return sequence?.id === locator.sequenceId ? sequence : undefined;
  }

  return findInteractionSequence(take.interactions ?? [], locator.sequenceId);
}

function sameLocator(a: SequenceLocator, b: SequenceLocator) {
  return (
    a.kind === b.kind &&
    a.takeId === b.takeId &&
    a.sequenceId === b.sequenceId
  );
}

function replaceLocatedSequence(
  takes: Take[],
  locator: SequenceLocator,
  replacement: ISequence,
): Take[] {
  return takes.map((take) => {
    if (take.id !== locator.takeId) return take;
    if (locator.kind === "takeSequence") {
      return { ...take, sequence: replacement } as Take3D;
    }

    return {
      ...take,
      interactions: (take.interactions ?? []).map((interaction) =>
        mapInteractionTree(interaction, (candidate) =>
          candidate.id === locator.sequenceId ? replacement : candidate,
        ),
      ),
    };
  });
}

function translatePosition(
  position: { x: number; y: number },
  delta: { x: number; y: number },
) {
  return { x: position.x + delta.x, y: position.y + delta.y };
}

export function moveSequenceSelection(
  takes: Take[],
  payload: MoveSequenceSelectionPayload,
): SequenceMoveResolution {
  const source = resolveSequenceLocator(takes, payload.source);
  if (!source) return { ok: false, reason: "missingSource" };
  const destination = resolveSequenceLocator(takes, payload.destination);
  if (!destination) return { ok: false, reason: "missingDestination" };

  const nodeIds = new Set(payload.selection.nodeIds);
  const commentZoneIds = new Set(payload.selection.commentZoneIds);
  const textboxIds = new Set(payload.selection.textboxIds);
  if (nodeIds.size + commentZoneIds.size + textboxIds.size === 0) {
    return { ok: false, reason: "emptySelection" };
  }

  const sourceNodes = source.nodes ?? [];
  const sourceCommentZones = source.commentZones ?? [];
  const sourceTextboxes = source.textboxes ?? [];
  const movedNodes = sourceNodes.filter((node) => nodeIds.has(node.id));
  const movedCommentZones = sourceCommentZones.filter((zone) =>
    commentZoneIds.has(zone.id),
  );
  const movedTextboxes = sourceTextboxes.filter((textbox) =>
    textboxIds.has(textbox.id),
  );
  if (
    movedNodes.length !== nodeIds.size ||
    movedCommentZones.length !== commentZoneIds.size ||
    movedTextboxes.length !== textboxIds.size
  ) {
    return { ok: false, reason: "missingItems" };
  }

  const allPositions = [
    ...movedNodes.map((item) => item.position),
    ...movedCommentZones.map((item) => item.position),
    ...movedTextboxes.map((item) => item.position),
  ];
  const minX = Math.min(...allPositions.map((position) => position.x));
  const minY = Math.min(...allPositions.map((position) => position.y));
  const delta = {
    x: payload.targetOrigin.x - minX,
    y: payload.targetOrigin.y - minY,
  };
  const translatedNodes = movedNodes.map((node) => ({
    ...node,
    selected: true,
    position: translatePosition(node.position, delta),
  }));
  const translatedCommentZones = movedCommentZones.map((zone) => ({
    ...zone,
    position: translatePosition(zone.position, delta),
  }));
  const translatedTextboxes = movedTextboxes.map((textbox) => ({
    ...textbox,
    selected: true,
    position: translatePosition(textbox.position, delta),
  }));

  if (sameLocator(payload.source, payload.destination)) {
    const nodeById = new Map(translatedNodes.map((node) => [node.id, node]));
    const zoneById = new Map(
      translatedCommentZones.map((zone) => [zone.id, zone]),
    );
    const textboxById = new Map(
      translatedTextboxes.map((textbox) => [textbox.id, textbox]),
    );
    const nextSequence: ISequence = {
      ...source,
      nodes: sourceNodes.map((node) =>
        nodeById.get(node.id) ?? { ...node, selected: false },
      ),
      commentZones: sourceCommentZones.map(
        (zone) => zoneById.get(zone.id) ?? zone,
      ),
      textboxes: sourceTextboxes.map(
        (textbox) =>
          textboxById.get(textbox.id) ?? { ...textbox, selected: false },
      ),
    };
    return {
      ok: true,
      takes: replaceLocatedSequence(takes, payload.source, nextSequence),
      movedNodes: translatedNodes,
      movedCommentZones: translatedCommentZones,
      movedTextboxes: translatedTextboxes,
    };
  }

  const destinationNodeIds = new Set((destination.nodes ?? []).map((n) => n.id));
  const destinationInteractionIds = new Set(
    (destination.nodes ?? []).map((n) => n.data.interaction.id),
  );
  const destinationZoneIds = new Set(
    (destination.commentZones ?? []).map((zone) => zone.id),
  );
  const destinationTextboxIds = new Set(
    (destination.textboxes ?? []).map((textbox) => textbox.id),
  );
  if (
    movedNodes.some(
      (node) =>
        destinationNodeIds.has(node.id) ||
        destinationInteractionIds.has(node.data.interaction.id),
    ) ||
    movedCommentZones.some((zone) => destinationZoneIds.has(zone.id)) ||
    movedTextboxes.some((textbox) => destinationTextboxIds.has(textbox.id))
  ) {
    return { ok: false, reason: "destinationCollision" };
  }

  const internalEdges = (source.edges ?? []).filter(
    (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target),
  );
  const destinationEdgeIds = new Set((destination.edges ?? []).map((e) => e.id));
  const movedEdges = internalEdges.map((edge): Edge => {
    if (!destinationEdgeIds.has(edge.id)) {
      destinationEdgeIds.add(edge.id);
      return edge;
    }
    let suffix = 1;
    let id = `${edge.id}-moved-${suffix}`;
    while (destinationEdgeIds.has(id)) {
      suffix += 1;
      id = `${edge.id}-moved-${suffix}`;
    }
    destinationEdgeIds.add(id);
    return { ...edge, id };
  });

  const nextSource: ISequence = {
    ...source,
    nodes: sourceNodes.filter((node) => !nodeIds.has(node.id)),
    edges: (source.edges ?? []).filter(
      (edge) => !nodeIds.has(edge.source) && !nodeIds.has(edge.target),
    ),
    commentZones: sourceCommentZones.filter(
      (zone) => !commentZoneIds.has(zone.id),
    ),
    textboxes: sourceTextboxes.filter((textbox) => !textboxIds.has(textbox.id)),
  };
  const nextDestination: ISequence = {
    ...destination,
    nodes: [
      ...(destination.nodes ?? []).map((node) => ({ ...node, selected: false })),
      ...translatedNodes,
    ],
    edges: [...(destination.edges ?? []), ...movedEdges],
    commentZones: [
      ...(destination.commentZones ?? []),
      ...translatedCommentZones,
    ],
    textboxes: [
      ...(destination.textboxes ?? []).map((textbox) => ({
        ...textbox,
        selected: false,
      })),
      ...translatedTextboxes,
    ],
  };

  let nextTakes = replaceLocatedSequence(takes, payload.source, nextSource);
  nextTakes = replaceLocatedSequence(nextTakes, payload.destination, nextDestination);
  return {
    ok: true,
    takes: nextTakes,
    movedNodes: translatedNodes,
    movedCommentZones: translatedCommentZones,
    movedTextboxes: translatedTextboxes,
  };
}
